import React from "react";
import toast from "react-hot-toast";

import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import {
  getDeliveryPhotoRequired,
  updateDeliveryPhotoRequired,
  getDumpReceiptRequired,
  updateDumpReceiptRequired,
  getPickupPhotoRequired,
  updatePickupPhotoRequired,
} from "../../../settingsSlice";

import "./CaptureSettingsSection.scss";

export function CaptureSettingsSection() {
  const dispatch = useAppDispatch();

  const deliveryPhoto = useAppSelector(
    (s) => s.settings.deliveryPhotoRequired
  );
  const dumpReceipt = useAppSelector(
    (s) => s.settings.dumpReceiptRequired
  );
  const deliverySaveAPI = useAppSelector(
    (s) => s.settings.updateDeliveryPhotoRequired
  );
  const dumpReceiptSaveAPI = useAppSelector(
    (s) => s.settings.updateDumpReceiptRequired
  );
  const pickupPhoto = useAppSelector(
    (s) => s.settings.pickupPhotoRequired
  );
  const pickupPhotoSaveAPI = useAppSelector(
    (s) => s.settings.updatePickupPhotoRequired
  );

  // Load all settings once on mount.
  React.useEffect(() => {
    dispatch(getDeliveryPhotoRequired());
    dispatch(getDumpReceiptRequired());
    dispatch(getPickupPhotoRequired());
  }, [dispatch]);

  const onToggleDeliveryPhoto = async () => {
    if (deliveryPhoto === undefined) return;
    const next = !deliveryPhoto;
    const result = await dispatch(updateDeliveryPhotoRequired(next));
    if (updateDeliveryPhotoRequired.fulfilled.match(result)) {
      toast.success(
        next
          ? "Delivery photo is now required."
          : "Delivery photo is now optional."
      );
    } else {
      toast.error(
        "Failed to update the delivery photo setting. Please try again."
      );
    }
  };

  const onToggleDumpReceipt = async () => {
    if (dumpReceipt === undefined) return;
    const next = !dumpReceipt;
    const result = await dispatch(updateDumpReceiptRequired(next));
    if (updateDumpReceiptRequired.fulfilled.match(result)) {
      toast.success(
        next
          ? "Dump receipt is now required."
          : "Dump receipt is now optional."
      );
    } else {
      toast.error(
        "Failed to update the dump receipt setting. Please try again."
      );
    }
  };

  const onTogglePickupPhoto = async () => {
    if (pickupPhoto === undefined) return;
    const next = !pickupPhoto;
    const result = await dispatch(updatePickupPhotoRequired(next));
    if (updatePickupPhotoRequired.fulfilled.match(result)) {
      toast.success(
        next
          ? "Pickup photo is now required."
          : "Pickup photo is now optional."
      );
    } else {
      toast.error(
        "Failed to update the pickup photo setting. Please try again."
      );
    }
  };

  return (
    <section className="da-section capture-settings-section">
      <div className="section-heading">
        <h3>Photo Requirements</h3>
        <span className="section-hint">
          The driver app always shows these photo steps. Turning one on makes that
          photo <strong>required</strong> — the driver can't complete the stop
          without it. Off means the photo is optional.
        </span>
      </div>

      <div className="toggle-list">
        <label className="toggle-row">
          <input
            type="checkbox"
            aria-label="Require delivery photo"
            checked={deliveryPhoto ?? false}
            disabled={deliveryPhoto === undefined || deliverySaveAPI.loading}
            onChange={onToggleDeliveryPhoto}
          />
          <span className="toggle-label">
            <span className="toggle-title">Require delivery photo</span>
            <span className="toggle-hint">
              Require a photo of the dumpster placement before a driver can
              complete a delivery.
            </span>
          </span>
        </label>

        <label className="toggle-row">
          <input
            type="checkbox"
            aria-label="Require dump receipt"
            checked={dumpReceipt ?? false}
            disabled={dumpReceipt === undefined || dumpReceiptSaveAPI.loading}
            onChange={onToggleDumpReceipt}
          />
          <span className="toggle-label">
            <span className="toggle-title">Require dump receipt</span>
            <span className="toggle-hint">
              Require a dump-yard receipt photo before a driver can complete a
              dump stop.
            </span>
          </span>
        </label>

        <label className="toggle-row">
          <input
            type="checkbox"
            aria-label="Require pickup photo"
            checked={pickupPhoto ?? false}
            disabled={pickupPhoto === undefined || pickupPhotoSaveAPI.loading}
            onChange={onTogglePickupPhoto}
          />
          <span className="toggle-label">
            <span className="toggle-title">Require pickup photo</span>
            <span className="toggle-hint">
              Require a photo of the dumpster before a driver can complete a
              pickup.
            </span>
          </span>
        </label>
      </div>
    </section>
  );
}
